<template>
    <div v-if="showBanner" class="unpaid-invoice-banner-wrapper">
        <transition name="slide-down">
            <div class="unpaid-invoice-banner" :class="{ 'banner-urgent': isUrgent }" v-if="bannerVisible">
                <div class="banner-main">
                    <div class="banner-content" @click="goToBilling">
                        <span class="material-icons warning-icon">warning</span>
                        <div class="banner-text-wrapper">
                            <span class="banner-text">
                                {{ bannerMessage }}
                            </span>
                            <span class="banner-click-text">
                                {{ $t("billing.clickToPay") }}
                            </span>
                        </div>
                    </div>
                    <button class="close-btn" @click.stop="closeBanner">
                        <span class="material-icons">close</span>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { getInvoices } from "../../api/billing";
import { mapState } from "vuex";

const STORAGE_KEY = "unpaid-invoice-banner-closed";
const DAYS_THRESHOLD = 6; // Kalan gün sayısı bu değerden az olduğunda tüm kullanıcılara gösterilecek

export default {
    name: "UnpaidInvoiceBanner",
    data() {
        return {
            bannerVisible: false,
            shouldShowBanner: false,
            isLoading: false,
            earliestDueDate: null,
            daysRemaining: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            usersInitialized: (state) => state.user.users.initialized,
        }),
        isBannerClosed() {
            return sessionStorage.getItem(STORAGE_KEY) === "true";
        },
        isStateReady() {
            return !!this.user && this.usersInitialized === true;
        },
        isOwner() {
            // isOwner alanı true, 1 vb. truthy değerler olabilir
            return this.$_.get(this.user, "isOwner", false);
        },
        formattedDueDate() {
            if (!this.earliestDueDate) return "";
            const date = new Date(this.earliestDueDate);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        },
        bannerMessage() {
            if (!this.formattedDueDate) {
                return this.$t("billing.unpaidInvoiceBannerMessage");
            }
            return this.$t("billing.unpaidInvoiceBannerMessageWithDate", { date: this.formattedDueDate });
        },
        showBanner() {
            if (!this.isStateReady) {
                console.log("[UnpaidInvoiceBanner] isStateReady = false");
                return false;
            }
            if (!this.shouldShowBanner) {
                console.log("[UnpaidInvoiceBanner] shouldShowBanner = false");
                return false;
            }
            if (this.isBannerClosed) {
                console.log("[UnpaidInvoiceBanner] isBannerClosed = true");
                return false;
            }
            
            // Son ödeme tarihine 6 gün veya daha fazla varsa sadece owner kullanıcıya göster
            if (this.daysRemaining !== null && this.daysRemaining >= DAYS_THRESHOLD) {
                console.log("[UnpaidInvoiceBanner] Son ödeme tarihine 6+ gün var, sadece owner'a gösteriliyor:", {
                    daysRemaining: this.daysRemaining,
                    isOwner: this.isOwner,
                });
                if (!this.isOwner) {
                    console.log("[UnpaidInvoiceBanner] Owner değil, gizleniyor");
                    return false;
                }
            }
            
            console.log("[UnpaidInvoiceBanner] Banner gösteriliyor! ✅");
            return true;
        },
        isUrgent() {
            // Son 1 gün veya daha az kaldıysa acil durum
            return this.daysRemaining !== null && this.daysRemaining <= 1;
        },
    },
    methods: {
        closeBanner() {
            this.bannerVisible = false;
            sessionStorage.setItem(STORAGE_KEY, "true");
        },
        goToBilling() {
            this.$router.push("/settings/billing");
        },
        calculateDaysRemaining(dueDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const due = new Date(dueDate);
            due.setHours(0, 0, 0, 0);
            const diffTime = due.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        },
        filterValidInvoices(invoices) {
            if (!invoices || invoices.length === 0) return [];
            // invalidPayment: true olan faturaları filtrele (geçersiz işaretlenmiş faturalar)
            // Sadece invalidPayment: false olanları al
            return invoices.filter((inv) => inv.invalidPayment !== true);
        },
        findEarliestDueDate(invoices) {
            if (!invoices || invoices.length === 0) return null;
            
            // dueDate'i olan faturaları filtrele
            const invoicesWithDueDate = invoices.filter((inv) => inv.dueDate);
            if (invoicesWithDueDate.length === 0) return null;

            // En erken dueDate'i bul
            let earliest = invoicesWithDueDate[0].dueDate;
            for (const invoice of invoicesWithDueDate) {
                if (new Date(invoice.dueDate) < new Date(earliest)) {
                    earliest = invoice.dueDate;
                }
            }
            return earliest;
        },
        async checkUnpaidInvoices() {
            if (!this.isStateReady) {
                this.shouldShowBanner = false;
                return;
            }

            try {
                this.isLoading = true;
                const response = await getInvoices(
                    { type: "unpaid" },
                    {
                        page: 1,
                        perPage: 100,
                    }
                );

                const { invoices = [] } = response.data || {};

                // Debug: Fatura yapısını görmek için
                if (invoices.length > 0) {
                    console.log("[UnpaidInvoiceBanner] İlk fatura örneği:", JSON.stringify(invoices[0], null, 2));
                }

                // invalidPayment: true olan faturaları filtrele
                const validInvoices = this.filterValidInvoices(invoices);

                // Geçerli ödenmemiş fatura yoksa banner gösterme
                if (validInvoices.length === 0) {
                    this.shouldShowBanner = false;
                    return;
                }

                // En erken dueDate'i bul (geçerli faturalardan)
                this.earliestDueDate = this.findEarliestDueDate(validInvoices);

                if (!this.earliestDueDate) {
                    this.shouldShowBanner = false;
                    return;
                }

                // Kalan gün sayısını hesapla
                this.daysRemaining = this.calculateDaysRemaining(this.earliestDueDate);

                // Debug için console log
                console.log("[UnpaidInvoiceBanner] Debug Info:", {
                    earliestDueDate: this.earliestDueDate,
                    daysRemaining: this.daysRemaining,
                    DAYS_THRESHOLD,
                    isOwner: this.isOwner,
                });

                // Ödenmemiş fatura varsa banner göster
                // showBanner computed'ı owner kontrolünü yapacak
                this.shouldShowBanner = true;
                if (!this.isBannerClosed) {
                    this.bannerVisible = true;
                }
            } catch (error) {
                console.error("Ödenmemiş fatura kontrolü hatası:", error);
                this.shouldShowBanner = false;
            } finally {
                this.isLoading = false;
            }
        },
    },
    watch: {
        isStateReady: {
            handler(newVal) {
                if (newVal && !this.shouldShowBanner && !this.isLoading) {
                    this.checkUnpaidInvoices();
                }
            },
            immediate: true,
        },
    },
    mounted() {
        if (this.isStateReady) {
            this.checkUnpaidInvoices();
        }
    },
};
</script>

<style lang="scss" scoped>
.unpaid-invoice-banner-wrapper {
    position: fixed;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    width: 600px;
    max-width: calc(100vw - 20px);
}

.unpaid-invoice-banner {
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
    border: 1px solid #ffc107;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    transition: all 0.3s ease;

    .banner-main {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 12px 16px;
    }

    .banner-content {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        flex: 1;
        min-width: 0;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.8;
        }

        .warning-icon {
            color: #f5a623;
            font-size: 20px;
            flex-shrink: 0;
            margin-top: 2px;
        }

        .banner-text-wrapper {
            display: flex;
            flex-direction: column;
            gap: 6px;
            min-width: 0;
            flex: 1;
        }

        .banner-text {
            color: #856404;
            font-size: 13px;
            font-weight: 500;
            line-height: 1.5;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }

        .banner-click-text {
            color: #0066cc;
            font-size: 13px;
            font-weight: 600;
            text-decoration: underline;
            flex-shrink: 0;

            &:hover {
                color: #004499;
            }
        }
    }

    .close-btn {
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.2s;
        flex-shrink: 0;

        &:hover {
            background: rgba(0, 0, 0, 0.1);
        }

        .material-icons {
            font-size: 18px;
            color: #856404;
        }
    }

    // Son 1 gün kaldığında kırmızı tema
    &.banner-urgent {
        background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
        border-color: #dc3545;

        .banner-content {
            .warning-icon {
                color: #dc3545;
            }

            .banner-text {
                color: #721c24;
            }

            .banner-click-text {
                color: #721c24;

                &:hover {
                    color: #491217;
                }
            }
        }

        .close-btn {
            .material-icons {
                color: #721c24;
            }
        }
    }
}

.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}
</style>
